'use client'

import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react'
import React, {
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from 'react'

import { Button } from '../ui/Button'
import { cn } from '../ui/cn'

// Tolerance (px) to absorb sub-pixel layout rounding and scrollbar jitter when
// deciding whether a card counts as fully visible.
const TOL = 2

// How far a swipe must travel before releasing it pages instead of snapping
// back, and the slop below which a touch is still a tap rather than a drag.
const SWIPE_THRESHOLD = 40
const TAP_SLOP = 8

interface FeedPaginatorProps {
  /** Cards to page through. Each child is measured as one card. */
  children: ReactNode
  prevLabel?: string
  nextLabel?: string
}

/**
 * Track of cards that pages by whole cards, plus its prev/next controls.
 * Ported verbatim from playground `@ui_patterns/FeedPaginator` — replaces
 * the two ad-hoc carousel implementations that had drifted (BlogCards +
 * CustomerReviews).
 *
 * Renders no section or heading of its own — a wrapper supplies those. The
 * cards are expected to overflow their parent and be clipped further out by
 * an ancestor with hidden overflow; both edges are measured from the live
 * DOM.
 */
export const FeedPaginator = ({
  children,
  prevLabel = 'Previous',
  nextLabel = 'Next',
}: FeedPaginatorProps): React.ReactElement => {
  const trackRef = useRef<HTMLDivElement>(null)
  const [offset, setOffset] = useState(0)
  const [maxOffset, setMaxOffset] = useState(0)
  // The in-progress swipe: which pointer owns it and where it started. Held in
  // a ref because the move handler needs the value it was given synchronously.
  const swipe = useRef<{ id: number; startX: number } | null>(null)
  // Whether the current touch has moved far enough to be a drag rather than a
  // tap, so the click it ends with can be kept from opening a card.
  const dragged = useRef(false)
  const [dragging, setDragging] = useState(false)
  const [drag, setDrag] = useState(0)

  // Read the live layout of every card. Card positions come from
  // offsetLeft/offsetWidth, which are unaffected by the track's translateX
  // transform, so they stay stable no matter where the carousel is scrolled.
  const readCards = (): {
    lefts: number[]
    rights: number[]
    viewport: number
    maxOffset: number
  } | null => {
    const track = trackRef.current
    const parent = track?.parentElement
    if (!track || !parent) return null
    const cards = Array.from(track.children) as HTMLElement[]
    if (cards.length === 0) return null

    const base = cards[0].offsetLeft
    const lefts = cards.map((c) => c.offsetLeft - base)
    const rights = cards.map((c, i) => lefts[i] + c.offsetWidth)

    // The cards overflow the parent and are only clipped further out, so the
    // visible width runs from the track's left all the way to that clip
    // edge — wider than the track's own content box. Both edges are
    // measured off untransformed elements so a slide in progress can't
    // skew them.
    const viewport = clipRight(track) - contentLeft(parent)

    // Every page left-aligns to a card, including the last one. The furthest
    // we slide is the first card from which the final card is fully
    // visible, so a short final page shows trailing empty space instead of
    // right-aligning.
    const contentWidth = rights[rights.length - 1]
    const limit = contentWidth - viewport
    const maxIndex = lefts.findIndex((l) => l >= limit - TOL)
    return {
      lefts,
      rights,
      viewport,
      maxOffset: maxIndex === -1 ? 0 : lefts[maxIndex],
    }
  }

  // Next: slide the first card clipped by the right edge to the left edge. A
  // "page" is whatever fully fits, so this reveals the next batch of full
  // cards.
  const goNext = (): void => {
    const g = readCards()
    if (!g) return
    const clipped = g.rights.findIndex((r) => r > offset + g.viewport + TOL)
    setOffset(clipped === -1 ? g.maxOffset : Math.min(g.lefts[clipped], g.maxOffset))
  }

  // Prev: pack the widest run of cards that ends just before the current
  // left edge and fits the viewport, then align it to the left.
  const goPrev = (): void => {
    const g = readCards()
    if (!g) return
    const first = g.lefts.findIndex((l) => l >= offset - TOL)
    if (first <= 0) {
      setOffset(0)
      return
    }
    const last = first - 1
    let start = last
    while (start > 0 && g.rights[last] - g.lefts[start - 1] <= g.viewport + TOL) {
      start -= 1
    }
    setOffset(Math.max(0, g.lefts[start]))
  }

  // Swiping tracks the finger 1:1 and then pages in the direction it went, so
  // a swipe lands on exactly the page the matching button would have. Mouse
  // pointers are left alone: dragging a card with a mouse is not how the
  // pattern reads on desktop, and clicks and text selection stay untouched.
  const startSwipe = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (e.pointerType === 'mouse' || swipe.current || maxOffset <= TOL) return
    swipe.current = { id: e.pointerId, startX: e.clientX }
    dragged.current = false
    setDragging(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const moveSwipe = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (swipe.current?.id !== e.pointerId) return
    const delta = swipe.current.startX - e.clientX
    if (Math.abs(delta) > TAP_SLOP) dragged.current = true
    // Clamped against the same bounds the buttons respect, so the track can't
    // be pulled past either end.
    setDrag(Math.max(0, Math.min(offset + delta, maxOffset)) - offset)
  }

  const endSwipe = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (swipe.current?.id !== e.pointerId) return
    swipe.current = null
    setDragging(false)
    setDrag(0)
    // Paging works off `offset`, which the drag never touched, so a short swipe
    // simply animates back to where it started.
    if (drag >= SWIPE_THRESHOLD) goNext()
    else if (drag <= -SWIPE_THRESHOLD) goPrev()
  }

  // Fires when the browser takes the gesture over for vertical scrolling.
  const cancelSwipe = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (swipe.current?.id !== e.pointerId) return
    swipe.current = null
    setDragging(false)
    setDrag(0)
  }

  // A swipe that ends over a card still fires a click on it; swallow that one
  // so the gesture doesn't navigate.
  const swallowSwipeClick = (e: ReactMouseEvent<HTMLDivElement>): void => {
    if (!dragged.current) return
    dragged.current = false
    e.preventDefault()
    e.stopPropagation()
  }

  // Keep the scroll bounds current and clamp the offset when the viewport is
  // resized, so the pagination stays correct at any width.
  useLayoutEffect(() => {
    const track = trackRef.current
    if (!track) return

    const sync = (): void => {
      const g = readCards()
      if (!g) return
      setMaxOffset(g.maxOffset)
      setOffset((o) => Math.min(o, g.maxOffset))
    }

    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(track)
    return () => observer.disconnect()
  }, [])

  const atStart = offset <= TOL
  const atEnd = offset >= maxOffset - TOL

  return (
    <>
      <div
        ref={trackRef}
        // touch-pan-y hands vertical scrolling back to the browser and keeps
        // the horizontal axis for us; it cancels the swipe once the page
        // starts scrolling. The transition is dropped mid-drag so the track
        // stays under the finger instead of easing after it.
        className={cn(
          'snplay:mt-8 snplay:flex snplay:touch-pan-y snplay:gap-8',
          !dragging && 'snplay:transition-transform snplay:duration-500 snplay:ease-out',
        )}
        style={{ transform: `translateX(-${offset + drag}px)` }}
        onPointerDown={startSwipe}
        onPointerMove={moveSwipe}
        onPointerUp={endSwipe}
        onPointerCancel={cancelSwipe}
        onClickCapture={swallowSwipeClick}
      >
        {children}
      </div>
      <div className="snplay:mt-8 snplay:flex snplay:gap-2">
        <Button size="large" aria-label={prevLabel} disabled={atStart} onClick={goPrev}>
          <ArrowLeftIcon className="snplay:size-5" />
        </Button>
        <Button size="large" aria-label={nextLabel} disabled={atEnd} onClick={goNext}>
          <ArrowRightIcon className="snplay:size-5" />
        </Button>
      </div>
    </>
  )
}

// Viewport x of an element's content-box left edge — where the track's first
// card sits when the track is at offset 0.
const contentLeft = (el: HTMLElement): number => {
  const style = getComputedStyle(el)
  return el.getBoundingClientRect().left + el.clientLeft + (parseFloat(style.paddingLeft) || 0)
}

// Viewport x at which the overflowing cards are actually cut off: the padding
// edge of the nearest ancestor that hides horizontal overflow, falling back
// to the window's right edge when nothing clips them.
const clipRight = (track: HTMLElement): number => {
  for (let el: HTMLElement | null = track.parentElement; el; el = el.parentElement) {
    if (getComputedStyle(el).overflowX !== 'visible') {
      const rect = el.getBoundingClientRect()
      return rect.left + el.clientLeft + el.clientWidth
    }
  }
  return document.documentElement.clientWidth
}
